import { useState, useEffect } from 'react';
import axios from '@/lib/axios';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Pencil, Trash, Tag, Sparkles, Music, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TaxonomyItem {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  order?: number;
  isActive: boolean;
}

type TaxonomyKind = 'categories' | 'moods';

const KIND_LABEL: Record<TaxonomyKind, { singular: string; plural: string; endpoint: string; defaultColor: string }> = {
  categories: { singular: 'Kategori', plural: 'Kategoriler', endpoint: '/categories/all', defaultColor: '#6366f1' },
  moods:      { singular: 'Mood',     plural: 'Mood\'lar',    endpoint: '/moods/all',      defaultColor: '#a855f7' }
};

function TaxonomySection({ kind }: { kind: TaxonomyKind }) {
  const { toast } = useToast();
  const meta = KIND_LABEL[kind];

  const [items, setItems] = useState<TaxonomyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TaxonomyItem | null>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    color: meta.defaultColor,
    order: 0,
    isActive: true
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(meta.endpoint);
      setItems(data || []);
    } catch (err: any) {
      toast({ title: 'Yükleme hatası', description: err.response?.data?.message || 'Liste alınamadı', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [kind]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', color: meta.defaultColor, order: 0, isActive: true });
    setDialogOpen(true);
  };

  const openEdit = (item: TaxonomyItem) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description || '',
      color: item.color || meta.defaultColor,
      order: item.order || 0,
      isActive: item.isActive
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Ad gerekli', variant: 'destructive' });
      return;
    }
    const base = `/${kind}`;
    try {
      if (editing) {
        await axios.put(`${base}/${editing._id}`, form);
        toast({ title: 'Güncellendi' });
      } else {
        await axios.post(base, form);
        toast({ title: 'Oluşturuldu' });
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      toast({ title: 'Kaydedilemedi', description: err.response?.data?.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (item: TaxonomyItem) => {
    if (!confirm(`"${item.name}" silinsin mi?`)) return;
    try {
      await axios.delete(`/${kind}/${item._id}`);
      toast({ title: 'Silindi' });
      fetchItems();
    } catch (err: any) {
      toast({ title: 'Silinemedi', description: err.response?.data?.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {items.length} {meta.singular.toLowerCase()}
        </p>
        <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" />
          Yeni {meta.singular}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500 text-sm">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-slate-500 text-sm">Henüz {meta.singular.toLowerCase()} yok</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((item) => (
            <Card key={item._id} className={cn('p-4 flex items-start gap-3', !item.isActive && 'opacity-50')}>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: (item.color || meta.defaultColor) + '20' }}
              >
                {kind === 'categories'
                  ? <Tag className="h-5 w-5" style={{ color: item.color || meta.defaultColor }} />
                  : <Sparkles className="h-5 w-5" style={{ color: item.color || meta.defaultColor }} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-slate-900 truncate">{item.name}</h3>
                  {!item.isActive && <Badge variant="outline" className="text-xs">Pasif</Badge>}
                </div>
                <p className="text-xs text-slate-500 truncate">{item.slug}</p>
                {item.description && (
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{item.description}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => handleDelete(item)}>
                  <Trash className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `${meta.singular} Düzenle` : `Yeni ${meta.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ad</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={kind === 'categories' ? 'Perakende' : 'Chill'}
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Opsiyonel"
              />
            </div>
            <div>
              <Label>Sıra</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="w-24"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="text-sm">Aktif</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={handleSubmit}>{editing ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Style Preset Yönetimi ───────────────────────────────────────────
interface StylePreset {
  _id: string;
  category: { _id: string; name: string; slug: string };
  label: string;
  slug: string;
  mood: string[];
  genre: string[];
  prompt: string;
  order: number;
  isActive: boolean;
}

function StylePresetSection() {
  const { toast } = useToast();
  const [presets, setPresets] = useState<StylePreset[]>([]);
  const [categories, setCategories] = useState<TaxonomyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<StylePreset | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [form, setForm] = useState({
    category: '',
    label: '',
    mood: '',
    genre: '',
    prompt: '',
    order: 0,
    isActive: true
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [presetsRes, catsRes] = await Promise.all([
        axios.get('/style-presets/all'),
        axios.get('/categories/all')
      ]);
      setPresets(presetsRes.data || []);
      setCategories(catsRes.data || []);
    } catch (err: any) {
      toast({ title: 'Yükleme hatası', description: err.response?.data?.message || 'Liste alınamadı', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = filterCategory === 'all'
    ? presets
    : presets.filter(p => p.category?._id === filterCategory);

  const openCreate = () => {
    setEditing(null);
    setForm({ category: filterCategory !== 'all' ? filterCategory : '', label: '', mood: '', genre: '', prompt: '', order: 0, isActive: true });
    setDialogOpen(true);
  };

  const openEdit = (preset: StylePreset) => {
    setEditing(preset);
    setForm({
      category: preset.category?._id || '',
      label: preset.label,
      mood: preset.mood.join(', '),
      genre: preset.genre.join(', '),
      prompt: preset.prompt,
      order: preset.order,
      isActive: preset.isActive
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.label.trim() || !form.prompt.trim() || !form.category) {
      toast({ title: 'Label, prompt ve kategori zorunlu', variant: 'destructive' });
      return;
    }
    const payload = {
      category: form.category,
      label: form.label.trim(),
      mood: form.mood.split(',').map(s => s.trim()).filter(Boolean),
      genre: form.genre.split(',').map(s => s.trim()).filter(Boolean),
      prompt: form.prompt.trim(),
      order: form.order,
      isActive: form.isActive
    };
    try {
      if (editing) {
        await axios.put(`/style-presets/${editing._id}`, payload);
        toast({ title: 'Güncellendi' });
      } else {
        await axios.post('/style-presets', payload);
        toast({ title: 'Oluşturuldu' });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      toast({ title: 'Kaydedilemedi', description: err.response?.data?.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (preset: StylePreset) => {
    if (!confirm(`"${preset.label}" silinsin mi?`)) return;
    try {
      await axios.delete(`/style-presets/${preset._id}`);
      toast({ title: 'Silindi' });
      fetchData();
    } catch (err: any) {
      toast({ title: 'Silinemedi', description: err.response?.data?.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Tüm kategoriler" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm kategoriler</SelectItem>
              {categories.map(c => (
                <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-600">{filtered.length} preset</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" />
          Yeni Preset
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500 text-sm">Yükleniyor...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-slate-500 text-sm">Henüz preset yok</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((preset) => {
            const isExpanded = expandedId === preset._id;
            return (
              <Card key={preset._id} className={cn('overflow-hidden', !preset.isActive && 'opacity-50')}>
                <div className="flex items-center gap-3 p-4">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <Music className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-900">{preset.label}</h3>
                      <Badge variant="outline" className="text-[10px]">{preset.category?.name}</Badge>
                      {!preset.isActive && <Badge variant="outline" className="text-[10px]">Pasif</Badge>}
                    </div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {preset.mood.map(m => (
                        <span key={m} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">{m}</span>
                      ))}
                      {preset.genre.map(g => (
                        <span key={g} className="text-[10px] px-1.5 py-0.5 bg-slate-800 text-white rounded">{g}</span>
                      ))}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setExpandedId(isExpanded ? null : preset._id)}>
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(preset)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => handleDelete(preset)}>
                    <Trash className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 font-mono whitespace-pre-wrap">
                      {preset.prompt}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Preset Düzenle' : 'Yeni Preset'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stil Adı</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Örn: Slap House" />
            </div>
            <div>
              <Label>Prompt</Label>
              <Textarea value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} rows={4} placeholder="MusicGPT'ye gönderilecek prompt" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mood (virgülle)</Label>
                <Input value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} placeholder="Energetic, Emotional" />
              </div>
              <div>
                <Label>Genre (virgülle)</Label>
                <Input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} placeholder="Slap House, Bass House" />
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div className="w-24">
                <Label>Sıra</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="h-4 w-4" />
                <span className="text-sm">Aktif</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={handleSubmit}>{editing ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TaxonomyPage() {
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Taksonomi</h1>
        <p className="text-sm text-muted-foreground mt-1">
          AI playlist'lere atanacak kategoriler ve mood'ları yönet.
        </p>
      </div>

      <Tabs defaultValue="categories" className="w-full">
        <TabsList>
          <TabsTrigger value="categories">
            <Tag className="h-4 w-4 mr-2" />
            Kategoriler
          </TabsTrigger>
          <TabsTrigger value="moods">
            <Sparkles className="h-4 w-4 mr-2" />
            Mood'lar
          </TabsTrigger>
          <TabsTrigger value="presets">
            <Music className="h-4 w-4 mr-2" />
            Style Preset'ler
          </TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-6">
          <TaxonomySection kind="categories" />
        </TabsContent>
        <TabsContent value="moods" className="mt-6">
          <TaxonomySection kind="moods" />
        </TabsContent>
        <TabsContent value="presets" className="mt-6">
          <StylePresetSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
